import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { courseService } from '@/services/course.service';
import { Clock, Users, Star, BookOpen, Award, CheckCircle, Target, Video, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

export default function CourseDetails() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['courseDetails', id],
    queryFn: () => courseService.getCourseDetails(id),
    enabled: !!id,
  });

  if (isLoading) return <div>Loading course details...</div>;
  if (error) {
    toast.error('Failed to load course details.');
    return <div>Error loading course details.</div>;
  }
  if (!course) return <div>Course not found.</div>;

  const tabs = ['overview', 'curriculum', 'instructor', 'reviews'];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'curriculum':
        return (
          <div className="space-y-4">
            {(course.modules || []).map((module, index) => (
              <div key={module._id} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-6 py-4">
                  <h3 className="text-lg font-semibold">Section {index + 1}: {module.title}</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {/* Placeholder for lessons */}
                  <div className="px-6 py-4">No lessons available in this view.</div>
                </div>
              </div>
            ))}
          </div>
        );
      default: // Overview
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-4">Course Description</h3>
              <p className="text-gray-700 leading-relaxed">{course.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">What you'll learn</h4>
                <ul className="space-y-2">
                  {(course.learning_objectives || []).map((obj, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span>{obj.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Requirements</h4>
                <ul className="space-y-2">
                  {(course.prerequisites || []).map((req, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl text-white p-8">
        <h1 className="text-4xl font-bold mb-4">{course.title}</h1>
        <p className="text-xl text-gray-200 mb-6">{course.description}</p>
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /><span>{course.rating || 0}</span></div>
          <div className="flex items-center gap-2"><Users className="w-4 h-4" /><span>{course.enrolledUsers || 0} students</span></div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4" /><span>{course.duration || 0} minutes</span></div>
        </div>
      </div>

      <div className="card">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 border-b-2 font-medium text-sm capitalize ${activeTab === tab ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
